import { useCallback, useEffect, useMemo, useState } from 'react';

import { apiCallable } from '@src/util/cloud-functions/apiClient';

const presenterResponse = (response, parseData) => {
  if (!response?.data) return null;

  if (response?.data) {
    if (parseData) {
      const dataParsed = parseData(response.data);
      return dataParsed;
    }

    return response.data;
  }
  return null;
};

const useCloudFunction = ({
  params,
  dependencies = [],
  op,
  parseData = null,
  runFirstTime = true,
}) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const httpRef = useMemo(() => apiCallable(op), [op]);
  const getRequestHttps = useCallback(
    async values => {
      try {
        setLoading(true);
        setError(null);

        const res = await httpRef(values);
        const resData = presenterResponse(res, parseData);
        setData(resData);

        return { success: true, data: resData };
      } catch (err) {
        const errorMessage = err?.message || 'An error occurred';
        setError(errorMessage);

        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [httpRef, parseData],
  );

  const fetchData = useCallback(() => {
    setLoading(true);
    httpRef(params)
      .then(response => {
        const output = presenterResponse(response, parseData);

        setData(output);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        setError(err);
      });
  }, [httpRef, params, parseData]);

  useEffect(() => {
    if (!runFirstTime) return;
    const areDependenciesValid = dependencies.every(dep => {
      if (typeof dep === 'object' && dep !== null) {
        return Object.keys(dep).length > 0;
      }
      if (Array.isArray(dep)) {
        return dep.length > 0;
      }

      if (typeof dep === 'boolean' || typeof dep === 'number') {
        return true;
      }

      return Boolean(dep);
    });

    if (areDependenciesValid) {
      setError(null);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return {
    data,
    loading,
    error,
    mutateAsync: getRequestHttps,
  };
};

export default useCloudFunction;
